import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBoxOpen,
  FaChevronRight,
  FaEdit,
  FaFileInvoice,
  FaHome,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegUser,
  FaUndo,
  FaSignOutAlt,
  FaTimes,
  FaTrashAlt,
  FaTruck,
} from "react-icons/fa";
import axios from "axios";
import useAuth from "../hooks/useAuth.js";
import useCart from "../hooks/useCart";
import useWishlist from "../hooks/useWishlist";
import { API_BASE, BACKEND_BASE } from "../config/api";
import "./AccountPage.css";

const ORDER_STEPS = [
  "Order Placed",
  "Confirmed",
  "Packed",
  "Shipped",
  "Out for Delivery",
  "Delivered",
];

const normalizeStatus = (status = "") => (status === "Processing" ? "Order Placed" : status || "Order Placed");

const formatCurrency = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "Rs. 0";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const formatDate = (value) => {
  if (!value) {
    return "Pending";
  }

  const dateValue = new Date(value);

  if (Number.isNaN(dateValue.getTime())) {
    return "Pending";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(dateValue);
};

const getImageUrl = (imagePath = "") => {
  if (!imagePath) {
    return "";
  }

  if (/^data:/i.test(imagePath) || /^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  if (imagePath.startsWith("//")) {
    return `${window.location.protocol}${imagePath}`;
  }

  const normalizedPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
  return new URL(normalizedPath, BACKEND_BASE).toString();
};

const addressToText = (address) => {
  if (!address) {
    return "";
  }

  return [
    address.fullName,
    address.addressLine1,
    address.addressLine2,
    [address.city, address.state].filter(Boolean).join(", "),
    address.pincode,
    address.country,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
};

const getPaymentStatus = (order) => {
  if (order?.orderStatus === "Payment Failed") {
    return "Payment Failed";
  }

  if (order?.orderStatus === "Payment Not Completed") {
    return "Payment Not Completed";
  }

  return order?.isPaid ? "Paid" : "Unpaid";
};

const emptyAddressForm = {
  fullName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
  phone: "",
  country: "India",
  isDefault: true,
};

const AccountPage = () => {
  const { user, loading, logout, setUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { clearWishlist } = useWishlist();
  const [activeSection, setActiveSection] = useState("personal");
  const [orders, setOrders] = useState([]);
  const [returnRequests, setReturnRequests] = useState([]);
  const [returnReasons, setReturnReasons] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [pageError, setPageError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [pincodeModalOpen, setPincodeModalOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingPincode, setSavingPincode] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState("");
  const [profileForm, setProfileForm] = useState({ name: "", phone: "", email: "" });
  const [addressForm, setAddressForm] = useState(emptyAddressForm);
  const [pincodeForm, setPincodeForm] = useState("");
  const [formError, setFormError] = useState("");
  const [returnModalData, setReturnModalData] = useState(null);
  const [returnForm, setReturnForm] = useState({ returnReason: "", description: "", unboxingVideo: null, images: [] });
  const [submittingReturn, setSubmittingReturn] = useState(false);

  const defaultAddress = useMemo(
    () => {
      const permanentAddress = user?.permanentAddress;
      if (permanentAddress?.addressLine1) {
        return {
          ...emptyAddressForm,
          ...permanentAddress,
          fullName: permanentAddress.fullName || user?.name || "",
          phone: permanentAddress.phone || user?.phone || "",
          isDefault: true,
        };
      }

      return addresses.find((address) => address.isDefault) || addresses[0] || null;
    },
    [addresses, user]
  );

  const deliveryPincode = defaultAddress?.pincode || user?.deliveryPincode || "";

  const orderedProducts = useMemo(
    () =>
      orders.flatMap((order) =>
        (order.orderItems || []).map((item, index) => ({
          key: `${order._id}-${index}`,
          order,
          item,
          index,
        }))
      ),
    [orders]
  );

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) {
      return;
    }

    setProfileForm({
      name: user.name || "",
      phone: user.phone || "",
      email: user.email || "",
    });
    setPincodeForm(user.deliveryPincode || "");
    fetchOrders();
    fetchAddresses();
    fetchReturns();
    fetchReturnReasons();
  }, [user]);

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await axios.get(API_BASE, { withCredentials: true });
      const nextOrders = Array.isArray(response.data) ? response.data : [];
      setOrders(
        nextOrders.filter(
          (order) => !["Payment Failed", "Payment Not Completed"].includes(order.orderStatus)
        )
      );
    } catch (error) {
      console.error("Error fetching orders:", error);
      setPageError("We could not load your orders right now.");
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      setLoadingAddresses(true);
      const response = await axios.get(`${API_BASE}/addresses`, { withCredentials: true });
      setAddresses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setPageError("We could not load your saved address right now.");
    } finally {
      setLoadingAddresses(false);
    }
  };

  const fetchReturns = async () => {
    try {
      const response = await axios.get(`${API_BASE}/returns`, { withCredentials: true });
      setReturnRequests(Array.isArray(response.data?.returns) ? response.data.returns : []);
    } catch (error) {
      console.error("Error fetching returns:", error);
    }
  };

  const fetchReturnReasons = async () => {
    try {
      const response = await axios.get(`${API_BASE}/return-reasons`, { withCredentials: true });
      const reasons = Array.isArray(response.data?.reasons) ? response.data.reasons : [];
      setReturnReasons(reasons);
      setReturnForm((form) => ({ ...form, returnReason: form.returnReason || reasons[0] || "" }));
    } catch (error) {
      console.error("Error fetching return reasons:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    clearCart();
    clearWishlist();
    navigate("/login");
  };

  const handleDeleteAccount = async () => {
    setFormError("");
    setDeletingAccount(true);

    try {
      await axios.post(`${API_BASE}/v1/user/delete-account`, null, { withCredentials: true });
      setUser?.(null);
      clearCart();
      clearWishlist();
      navigate("/");
    } catch (error) {
      setFormError(error.response?.data?.message || "Could not delete your account. Please try again.");
    } finally {
      setDeletingAccount(false);
    }
  };

  const openProfileModal = () => {
    setFormError("");
    setProfileForm({
      name: user?.name || "",
      phone: user?.phone || "",
      email: user?.email || "",
    });
    setProfileModalOpen(true);
  };

  const openAddressModal = () => {
    setFormError("");
    setAddressForm(defaultAddress ? { ...emptyAddressForm, ...defaultAddress } : emptyAddressForm);
    setAddressModalOpen(true);
  };

  const openPincodeModal = () => {
    setFormError("");
    setPincodeForm(deliveryPincode || "");
    setPincodeModalOpen(true);
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!profileForm.name.trim() || !profileForm.phone.trim() || !profileForm.email.trim()) {
      setFormError("Name, phone number, and email are required.");
      return;
    }

    setSavingProfile(true);

    try {
      const response = await axios.put(`${API_BASE}/v1/user/profile`, profileForm, {
        withCredentials: true,
      });
      const updatedUser = response.data?.user;

      if (updatedUser) {
        setUser?.(updatedUser);
      } else {
        await refreshUser?.();
      }

      setProfileModalOpen(false);
    } catch (error) {
      setFormError(error.response?.data?.message || "Could not update your profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddressSave = async (event) => {
    event.preventDefault();
    setFormError("");

    const requiredFields = ["fullName", "addressLine1", "city", "state", "pincode", "phone", "country"];
    const hasMissingField = requiredFields.some((field) => !String(addressForm[field] || "").trim());

    if (hasMissingField) {
      setFormError("Please fill all required address fields.");
      return;
    }

    if (!/^\d{6}$/.test(addressForm.pincode)) {
      setFormError("Pincode must be 6 digits.");
      return;
    }

    if (!/^\d{10}$/.test(addressForm.phone)) {
      setFormError("Phone number must be 10 digits.");
      return;
    }

    setSavingAddress(true);

    try {
      const response = await axios.put(
        `${API_BASE}/v1/user/profile`,
        { permanentAddress: addressForm },
        { withCredentials: true }
      );
      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        await refreshUser();
      }
      await fetchAddresses();
      setAddressModalOpen(false);
    } catch (error) {
      setFormError(error.response?.data?.message || "Could not save your address.");
    } finally {
      setSavingAddress(false);
    }
  };

  const handlePincodeSave = async (event) => {
    event.preventDefault();
    const nextPincode = String(pincodeForm || "").trim();
    setFormError("");

    if (!/^\d{6}$/.test(nextPincode)) {
      setFormError("Pincode must be 6 digits.");
      return;
    }

    setSavingPincode(true);

    try {
      const response = await axios.put(
        `${API_BASE}/v1/user/profile`,
        { deliveryPincode: nextPincode },
        { withCredentials: true }
      );

      if (response.data?.user) {
        setUser?.(response.data.user);
      }

      if (defaultAddress?._id) {
        await axios.put(
          `${API_BASE}/addresses/${defaultAddress._id}`,
          { ...defaultAddress, pincode: nextPincode },
          { withCredentials: true }
        );
        await fetchAddresses();
      }

      setPincodeModalOpen(false);
    } catch (error) {
      setFormError(error.response?.data?.message || "Could not save your pincode.");
    } finally {
      setSavingPincode(false);
    }
  };

  const showOrderDetails = (order, item) => {
    setSelectedOrder(order);
    setSelectedItem(item);
  };

  const closeOrderDetails = () => {
    setSelectedOrder(null);
    setSelectedItem(null);
  };

  const getReturnRequestForItem = (order, item) =>
    returnRequests.find((request) =>
      String(request.order?._id || request.order) === String(order?._id)
      && String(request.orderItemId) === String(item?._id)
    ) || null;

  const getReturnStateForItem = (order, item) => {
    const existingReturn = getReturnRequestForItem(order, item);

    if (existingReturn) {
      return { status: "submitted", request: existingReturn, message: "Return request submitted." };
    }

    if (order?.orderStatus !== "Delivered" || !order?.deliveredAt) {
      return { status: "unavailable", message: "" };
    }

    const expiryTime = new Date(order.deliveredAt).getTime() + 48 * 60 * 60 * 1000;

    if (Date.now() > expiryTime) {
      return { status: "expired", message: "Return period has expired." };
    }

    return { status: "eligible", message: "" };
  };

  const openReturnModal = (order, item, event) => {
    event?.stopPropagation();
    const returnState = getReturnStateForItem(order, item);

    if (returnState.status !== "eligible") {
      return;
    }

    setFormError("");
    setReturnForm({
      returnReason: returnReasons[0] || "",
      description: "",
      unboxingVideo: null,
      images: [],
    });
    setReturnModalData({ order, item });
  };

  const closeReturnModal = () => {
    setReturnModalData(null);
    setFormError("");
  };

  const handleReturnSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!returnForm.returnReason) {
      setFormError("Please select a return reason.");
      return;
    }

    if (!returnForm.unboxingVideo) {
      setFormError("Unboxing/Unpacking video is mandatory.");
      return;
    }

    setSubmittingReturn(true);

    try {
      const payload = new FormData();
      payload.append("orderId", returnModalData.order._id);
      payload.append("orderItemId", returnModalData.item._id);
      payload.append("returnReason", returnForm.returnReason);
      payload.append("description", returnForm.description);
      payload.append("unboxingVideo", returnForm.unboxingVideo);
      Array.from(returnForm.images || []).forEach((image) => payload.append("images", image));

      await axios.post(`${API_BASE}/returns`, payload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchReturns();
      closeReturnModal();
      setActiveSection("returns");
    } catch (error) {
      setFormError(error.response?.data?.message || "Could not submit your return request.");
    } finally {
      setSubmittingReturn(false);
    }
  };

  const hasInvoice = (order) => Boolean(order?._id && order?.isPaid);

  const isValidInvoiceNumber = (value = "") =>
    /^\d{2,}$/.test(String(value || "").trim());

  const getInvoiceNumberLabel = (orderData = {}) => {
    const existingInvoiceNumber = String(orderData.invoiceNumber || "").trim();
    if (isValidInvoiceNumber(existingInvoiceNumber)) {
      return `Invoice No.: ${existingInvoiceNumber}`;
    }

    return "Not generated yet";
  };

  const getInvoiceDownloadFilename = (response, fallback = "panchali-gst-invoice.pdf") => {
    const contentDisposition = String(response?.headers?.["content-disposition"] || "");
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || fallback;
  };

  const handleDownloadInvoice = async (order, event) => {
    event?.stopPropagation();

    if (!order?._id || !hasInvoice(order)) {
      return;
    }

    setDownloadingInvoiceId(order._id);

    try {
      const response = await axios.get(`${API_BASE}/${order._id}/invoice/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = getInvoiceDownloadFilename(response);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      const invoiceNumber = String(response?.headers?.["x-invoice-number"] || "").trim();
      if (invoiceNumber) {
        setOrders((currentOrders) =>
          currentOrders.map((currentOrder) =>
            currentOrder._id === order._id ? { ...currentOrder, invoiceNumber } : currentOrder
          )
        );
        setSelectedOrder((currentOrder) =>
          currentOrder?._id === order._id ? { ...currentOrder, invoiceNumber } : currentOrder
        );
      } else {
        fetchOrders();
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      setPageError(error.response?.data?.message || "We could not download this invoice right now.");
    } finally {
      setDownloadingInvoiceId("");
    }
  };

  const renderPersonalDetails = () => (
    <section className="account-panel">
      <div className="account-panel-header">
        <div>
          <span className="account-kicker">Profile</span>
          <h2>Personal Details</h2>
        </div>
        <button type="button" className="account-soft-btn" onClick={openProfileModal}>
          <FaEdit /> Edit Profile
        </button>
      </div>

      <div className="account-detail-grid">
        <div className="account-info-tile">
          <span>Name</span>
          <strong>{user?.name || "Not added"}</strong>
        </div>
        <div className="account-info-tile">
          <span>Phone Number</span>
          <strong>{user?.phone || "Not added"}</strong>
        </div>
        <div className="account-info-tile">
          <span>Email</span>
          <strong>{user?.email || "Not added"}</strong>
        </div>
      </div>
    </section>
  );

  const renderOrders = () => (
    <section className="account-panel">
      <div className="account-panel-header">
        <div>
          <span className="account-kicker">Purchases</span>
          <h2>My Orders</h2>
        </div>
        <button type="button" className="account-soft-btn" onClick={fetchOrders} disabled={loadingOrders}>
          <FaTruck /> {loadingOrders ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {loadingOrders ? (
        <div className="account-empty-state">Loading your orders...</div>
      ) : orderedProducts.length === 0 ? (
        <div className="account-empty-state">
          <strong>No orders yet</strong>
          <span>Your Panchali Jewels purchases will appear here.</span>
        </div>
      ) : (
        <div className="account-order-list">
          {orderedProducts.map(({ key, order, item }) => {
            const status = normalizeStatus(order.orderStatus);
            const imageUrl = getImageUrl(item.image || item.product?.images?.[0]);
            const returnState = getReturnStateForItem(order, item);

            return (
              <button
                type="button"
                key={key}
                className="account-order-card"
                onClick={() => showOrderDetails(order, item)}
              >
                <div className="account-order-image">
                  {imageUrl ? <img src={imageUrl} alt={item.title || "Ordered product"} /> : <span>No image</span>}
                </div>
                <div className="account-order-main">
                  <h3>{item.title || "Jewelry Item"}</h3>
                  <div className="account-order-meta">
                    <span>Order ID: #{order._id?.slice(-10)}</span>
                    <span>Order date: {formatDate(order.createdAt)}</span>
                    <span>Quantity: {item.quantity || 1}</span>
                  </div>
                </div>
                <div className="account-order-summary">
                  <strong>{formatCurrency(Number(item.price || 0) * Number(item.quantity || 1))}</strong>
                  <span className={`account-payment-pill ${order.isPaid ? "is-paid" : "is-unpaid"}`}>
                    {getPaymentStatus(order)}
                  </span>
                  <span className="account-status-pill">{status}</span>
                  {hasInvoice(order) ? (
                    <>
                      <span className="account-invoice-number">
                        {getInvoiceNumberLabel(order)}
                      </span>
                      <button
                        type="button"
                        className="account-invoice-download"
                        onClick={(event) => handleDownloadInvoice(order, event)}
                        disabled={downloadingInvoiceId === order._id}
                      >
                        <FaFileInvoice />
                        {downloadingInvoiceId === order._id ? "Downloading..." : "Invoice"}
                      </button>
                    </>
                  ) : null}
                  <span className="account-track-link">
                    Track Order <FaChevronRight />
                  </span>
                  {returnState.status === "eligible" ? (
                    <button
                      type="button"
                      className="account-return-btn"
                      onClick={(event) => openReturnModal(order, item, event)}
                    >
                      <FaUndo /> Return
                    </button>
                  ) : null}
                  {returnState.status === "submitted" ? (
                    <button
                      type="button"
                      className="account-return-status-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        setActiveSection("returns");
                      }}
                    >
                      View Return Status
                    </button>
                  ) : null}
                  {returnState.status === "expired" ? (
                    <span className="account-return-expired">Return period has expired.</span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );

  const renderAddress = () => (
    <section className="account-panel">
      <div className="account-panel-header">
        <div>
          <span className="account-kicker">Profile</span>
          <h2>Permanent Address</h2>
        </div>
        <button type="button" className="account-soft-btn" onClick={openAddressModal}>
          <FaEdit /> {defaultAddress ? "Edit Permanent Address" : "Add Permanent Address"}
        </button>
      </div>

      {loadingAddresses ? (
        <div className="account-empty-state">Loading permanent address...</div>
      ) : defaultAddress ? (
        <div className="account-address-card">
          <span className="account-address-label">Permanent Address</span>
          <strong>{defaultAddress.fullName}</strong>
          <p>{addressToText(defaultAddress)}</p>
          <span>Phone: {defaultAddress.phone}</span>
        </div>
      ) : (
        <div className="account-empty-state">
          <strong>No saved address</strong>
          <span>Add your permanent address for faster checkout.</span>
          <button type="button" onClick={openAddressModal}>
            Add Permanent Address
          </button>
        </div>
      )}
    </section>
  );

  const renderPincode = () => (
    <section className="account-panel">
      <div className="account-panel-header">
        <div>
          <span className="account-kicker">Serviceability</span>
          <h2>Pincode</h2>
        </div>
        <button type="button" className="account-soft-btn" onClick={openPincodeModal}>
          <FaEdit /> {deliveryPincode ? "Edit Pincode" : "Add Pincode"}
        </button>
      </div>

      <div className="account-pincode-card">
        <span>Delivery Pincode</span>
        <strong>{deliveryPincode || "Not added"}</strong>
      </div>
    </section>
  );

  const renderReturns = () => (
    <section className="account-panel">
      <div className="account-panel-header">
        <div>
          <span className="account-kicker">Product Returns</span>
          <h2>Returns</h2>
        </div>
        <button type="button" className="account-soft-btn" onClick={fetchReturns}>
          <FaUndo /> Refresh
        </button>
      </div>

      {returnRequests.length === 0 ? (
        <div className="account-empty-state">
          <strong>No return requests</strong>
          <span>Your submitted product returns will appear here.</span>
        </div>
      ) : (
        <div className="account-return-list">
          {returnRequests.map((request) => (
            <article className="account-return-card" key={request._id}>
              <div>
                <span>Product Name</span>
                <strong>{request.productName}</strong>
              </div>
              <div>
                <span>Order ID</span>
                <strong>#{String(request.order?._id || request.order).slice(-10)}</strong>
              </div>
              <div>
                <span>Invoice Number</span>
                <strong>{request.invoiceNumber || "Not generated"}</strong>
              </div>
              <div>
                <span>Return Request Date</span>
                <strong>{formatDate(request.returnRequestDateTime || request.createdAt)}</strong>
              </div>
              <div>
                <span>Return Reason</span>
                <strong>{request.returnReason}</strong>
              </div>
              <div>
                <span>Current Return Status</span>
                <strong className="account-return-status">Return Status: {request.returnStatus}</strong>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );

  const renderActiveSection = () => {
    if (activeSection === "orders") {
      return renderOrders();
    }

    if (activeSection === "address") {
      return renderAddress();
    }

    if (activeSection === "pincode") {
      return renderPincode();
    }

    if (activeSection === "returns") {
      return renderReturns();
    }

    return renderPersonalDetails();
  };

  const renderOrderModal = () => {
    if (!selectedOrder) {
      return null;
    }

    const status = normalizeStatus(selectedOrder.orderStatus);
    const activeStepIndex = ORDER_STEPS.indexOf(status);
    const addressText = addressToText(selectedOrder.shippingAddress);
    const selectedImage = getImageUrl(selectedItem?.image || selectedItem?.product?.images?.[0]);

    return (
      <div className="account-modal-overlay" onClick={closeOrderDetails}>
        <div className="account-order-modal" onClick={(event) => event.stopPropagation()}>
          <button type="button" className="account-modal-close" onClick={closeOrderDetails}>
            <FaTimes />
          </button>

          <div className="account-order-modal-head">
            <div>
              <span className="account-kicker">Order details</span>
              <h2>#{selectedOrder._id}</h2>
              <p>Placed on {formatDate(selectedOrder.createdAt)}</p>
            </div>
            <span className="account-status-pill">{status}</span>
          </div>

          <div className="account-modal-product">
            <div className="account-modal-product-image">
              {selectedImage ? <img src={selectedImage} alt={selectedItem?.title || "Ordered product"} /> : null}
            </div>
            <div>
              <h3>{selectedItem?.title || "Jewelry Item"}</h3>
              <p>Quantity: {selectedItem?.quantity || 1}</p>
              {selectedItem?.selectedSize ? <p>Size: {selectedItem.selectedSize}</p> : null}
              <strong>{formatCurrency(Number(selectedItem?.price || 0) * Number(selectedItem?.quantity || 1))}</strong>
            </div>
          </div>

          <div className="account-modal-grid">
            <div>
              <span>Delivery Address</span>
              <strong>{selectedOrder.shippingAddress?.fullName || "Saved address"}</strong>
              <p>{addressText || "Address unavailable"}</p>
              <p>Pincode: {selectedOrder.shippingAddress?.pincode || "--"}</p>
            </div>
            <div>
              <span>Payment Details</span>
              <strong>{selectedOrder.paymentMethod === "online" ? "Online Payment" : "Offline Payment"}</strong>
              <p>Status: {getPaymentStatus(selectedOrder)}</p>
              <p>Total: {formatCurrency(selectedOrder.totalPrice)}</p>
              {hasInvoice(selectedOrder) ? (
                <>
                  <p>{getInvoiceNumberLabel(selectedOrder)}</p>
                  <button
                    type="button"
                    className="account-invoice-download account-invoice-download--modal"
                    onClick={(event) => handleDownloadInvoice(selectedOrder, event)}
                    disabled={downloadingInvoiceId === selectedOrder._id}
                  >
                    <FaFileInvoice />
                    {downloadingInvoiceId === selectedOrder._id ? "Downloading..." : "Download Invoice"}
                  </button>
                </>
              ) : null}
            </div>
          </div>

          <div className="account-timeline">
            {ORDER_STEPS.map((step, index) => {
              const complete = selectedOrder.orderStatus !== "Cancelled" && activeStepIndex >= index;

              return (
                <div className={`account-timeline-step ${complete ? "is-complete" : ""}`} key={step}>
                  <span className="account-timeline-dot">{index + 1}</span>
                  <div>
                    <strong>{step}</strong>
                    <p>{complete ? (index === 0 ? formatDate(selectedOrder.createdAt) : "Updated") : "Pending"}</p>
                  </div>
                </div>
              );
            })}
            {selectedOrder.orderStatus === "Cancelled" ? (
              <div className="account-timeline-step is-cancelled">
                <span className="account-timeline-dot">!</span>
                <div>
                  <strong>Cancelled</strong>
                  <p>{selectedOrder.cancellationReason || "This order has been cancelled."}</p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="account-modal-items">
            <h3>All products in this order</h3>
            {(selectedOrder.orderItems || []).map((item, index) => (
              <div className="account-modal-item-row" key={`${selectedOrder._id}-modal-${index}`}>
                <span>{item.title}</span>
                <strong>
                  {item.quantity} x {formatCurrency(item.price)}
                </strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading || !user) {
    return <div className="account-loading">Loading your account...</div>;
  }

  const sidebarItems = [
    { key: "personal", label: "Personal Details", icon: <FaRegUser /> },
    { key: "orders", label: "My Orders", icon: <FaBoxOpen /> },
    { key: "returns", label: "Returns", icon: <FaUndo /> },
    { key: "address", label: "Permanent Address", icon: <FaHome /> },
    { key: "pincode", label: "Pincode", icon: <FaMapMarkerAlt /> },
  ];

  return (
    <div className="account-page">
      <div className="account-shell">
        <aside className="account-sidebar">
          <div className="account-profile-card">
            <div className="account-avatar">{user?.name?.charAt(0)?.toUpperCase() || "P"}</div>
            <div>
              <span>Hello,</span>
              <strong>{user?.name || "Panchali Customer"}</strong>
            </div>
          </div>

          <nav className="account-menu" aria-label="Account sections">
            {sidebarItems.map((item) => (
              <button
                type="button"
                key={item.key}
                className={activeSection === item.key ? "is-active" : ""}
                onClick={() => setActiveSection(item.key)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <button type="button" className="account-sidebar-delete" onClick={() => {
            setFormError("");
            setDeleteModalOpen(true);
          }}>
            <FaTrashAlt />
            <span>Delete Account</span>
          </button>

          <button type="button" className="account-sidebar-logout" onClick={handleLogout}>
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </aside>

        <main className="account-content">
          {pageError ? <div className="account-alert">{pageError}</div> : null}
          {renderActiveSection()}
        </main>
      </div>

      {profileModalOpen ? (
        <div className="account-modal-overlay" onClick={() => setProfileModalOpen(false)}>
          <form className="account-form-modal" onSubmit={handleProfileSave} onClick={(event) => event.stopPropagation()}>
            <button type="button" className="account-modal-close" onClick={() => setProfileModalOpen(false)}>
              <FaTimes />
            </button>
            <h2>Edit Profile</h2>
            <label>
              Name
              <input
                value={profileForm.name}
                onChange={(event) => setProfileForm((form) => ({ ...form, name: event.target.value }))}
              />
            </label>
            <label>
              Phone Number
              <input
                value={profileForm.phone}
                onChange={(event) =>
                  setProfileForm((form) => ({
                    ...form,
                    phone: event.target.value.replace(/[^\d]/g, "").slice(0, 10),
                  }))
                }
              />
            </label>
            <label>
              Email
              <input
                type="email"
                value={profileForm.email}
                onChange={(event) => setProfileForm((form) => ({ ...form, email: event.target.value }))}
              />
            </label>
            {formError ? <p className="account-form-error">{formError}</p> : null}
            <button type="submit" className="account-submit-btn" disabled={savingProfile}>
              {savingProfile ? "Saving..." : "Save Profile"}
            </button>
          </form>
        </div>
      ) : null}

      {addressModalOpen ? (
        <div className="account-modal-overlay" onClick={() => setAddressModalOpen(false)}>
          <form className="account-form-modal" onSubmit={handleAddressSave} onClick={(event) => event.stopPropagation()}>
            <button type="button" className="account-modal-close" onClick={() => setAddressModalOpen(false)}>
              <FaTimes />
            </button>
            <h2>{defaultAddress ? "Edit Permanent Address" : "Add Permanent Address"}</h2>
            <label>
              Full Name
              <input
                value={addressForm.fullName}
                onChange={(event) => setAddressForm((form) => ({ ...form, fullName: event.target.value }))}
              />
            </label>
            <label>
              Full Address
              <input
                value={addressForm.addressLine1}
                onChange={(event) => setAddressForm((form) => ({ ...form, addressLine1: event.target.value }))}
              />
            </label>
            <label>
              Address Line 2
              <input
                value={addressForm.addressLine2}
                onChange={(event) => setAddressForm((form) => ({ ...form, addressLine2: event.target.value }))}
              />
            </label>
            <div className="account-form-row">
              <label>
                City
                <input
                  value={addressForm.city}
                  onChange={(event) => setAddressForm((form) => ({ ...form, city: event.target.value }))}
                />
              </label>
              <label>
                State
                <input
                  value={addressForm.state}
                  onChange={(event) => setAddressForm((form) => ({ ...form, state: event.target.value }))}
                />
              </label>
            </div>
            <div className="account-form-row">
              <label>
                Pincode
                <input
                  value={addressForm.pincode}
                  onChange={(event) =>
                    setAddressForm((form) => ({
                      ...form,
                      pincode: event.target.value.replace(/[^\d]/g, "").slice(0, 6),
                    }))
                  }
                />
              </label>
              <label>
                <FaPhoneAlt /> Phone
                <input
                  value={addressForm.phone}
                  onChange={(event) =>
                    setAddressForm((form) => ({
                      ...form,
                      phone: event.target.value.replace(/[^\d]/g, "").slice(0, 10),
                    }))
                  }
                />
              </label>
            </div>
            <label>
              Country
              <input
                value={addressForm.country}
                onChange={(event) => setAddressForm((form) => ({ ...form, country: event.target.value }))}
              />
            </label>
            {formError ? <p className="account-form-error">{formError}</p> : null}
            <button type="submit" className="account-submit-btn" disabled={savingAddress}>
              {savingAddress ? "Saving..." : "Save Permanent Address"}
            </button>
          </form>
        </div>
      ) : null}

      {pincodeModalOpen ? (
        <div className="account-modal-overlay" onClick={() => setPincodeModalOpen(false)}>
          <form className="account-form-modal account-form-modal--compact" onSubmit={handlePincodeSave} onClick={(event) => event.stopPropagation()}>
            <button type="button" className="account-modal-close" onClick={() => setPincodeModalOpen(false)}>
              <FaTimes />
            </button>
            <h2>{deliveryPincode ? "Edit Pincode" : "Add Pincode"}</h2>
            <label>
              Delivery Pincode
              <input
                value={pincodeForm}
                onChange={(event) => setPincodeForm(event.target.value.replace(/[^\d]/g, "").slice(0, 6))}
              />
            </label>
            {formError ? <p className="account-form-error">{formError}</p> : null}
            <button type="submit" className="account-submit-btn" disabled={savingPincode}>
              {savingPincode ? "Saving..." : "Save Pincode"}
            </button>
          </form>
        </div>
      ) : null}

      {deleteModalOpen ? (
        <div className="account-modal-overlay" onClick={() => setDeleteModalOpen(false)}>
          <div className="account-form-modal account-form-modal--compact" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="account-modal-close" onClick={() => setDeleteModalOpen(false)}>
              <FaTimes />
            </button>
            <h2>Delete Account</h2>
            <p className="account-delete-copy">
              This will permanently delete your account and saved addresses. Your past orders may still be kept for billing and support records.
            </p>
            {formError ? <p className="account-form-error">{formError}</p> : null}
            <div className="account-danger-actions">
              <button
                type="button"
                className="account-cancel-btn"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deletingAccount}
              >
                Cancel
              </button>
              <button
                type="button"
                className="account-delete-confirm-btn"
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
              >
                {deletingAccount ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {returnModalData ? (
        <div className="account-modal-overlay" onClick={closeReturnModal}>
          <form className="account-form-modal account-return-form-modal" onSubmit={handleReturnSubmit} onClick={(event) => event.stopPropagation()}>
            <button type="button" className="account-modal-close" onClick={closeReturnModal}>
              <FaTimes />
            </button>
            <h2>Return Request</h2>
            <div className="account-return-form-product">
              <strong>{returnModalData.item.title}</strong>
              <span>Order ID: #{returnModalData.order._id}</span>
              <span>Invoice: {returnModalData.order.invoiceNumber || "Not generated"}</span>
            </div>
            <div className="account-form-row">
              <label>
                Customer Name
                <input value={user?.name || returnModalData.order.shippingAddress?.fullName || ""} readOnly />
              </label>
              <label>
                Phone Number
                <input value={user?.phone || returnModalData.order.shippingAddress?.phone || ""} readOnly />
              </label>
            </div>
            <label>
              Email
              <input value={user?.email || ""} readOnly />
            </label>
            <label>
              Address
              <input value={addressToText(returnModalData.order.shippingAddress)} readOnly />
            </label>
            <div className="account-form-row">
              <label>
                Product Name
                <input value={returnModalData.item.title || ""} readOnly />
              </label>
              <label>
                Vendor SKU
                <input value={returnModalData.item.vendorSku || returnModalData.item.vendorSKU || ""} readOnly />
              </label>
            </div>
            <label>
              Quantity
              <input value={returnModalData.item.quantity || 1} readOnly />
            </label>
            <label>
              Return Reason
              <select
                value={returnForm.returnReason}
                onChange={(event) => setReturnForm((form) => ({ ...form, returnReason: event.target.value }))}
              >
                {returnReasons.map((reason) => (
                  <option value={reason} key={reason}>{reason}</option>
                ))}
              </select>
            </label>
            <label>
              Description/Remarks
              <textarea
                value={returnForm.description}
                onChange={(event) => setReturnForm((form) => ({ ...form, description: event.target.value }))}
                rows="4"
              />
            </label>
            <label>
              Upload Unboxing/Unpacking Video (Mandatory)
              <input
                type="file"
                accept="video/*"
                onChange={(event) => setReturnForm((form) => ({ ...form, unboxingVideo: event.target.files?.[0] || null }))}
              />
            </label>
            <label>
              Upload Images (Optional)
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => setReturnForm((form) => ({ ...form, images: Array.from(event.target.files || []) }))}
              />
            </label>
            {formError ? <p className="account-form-error">{formError}</p> : null}
            <button type="submit" className="account-submit-btn" disabled={submittingReturn}>
              {submittingReturn ? "Submitting..." : "Submit Return Request"}
            </button>
          </form>
        </div>
      ) : null}

      {renderOrderModal()}
    </div>
  );
};

export default AccountPage;
